import React, { Component } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import VisibilitySensor from 'react-visibility-sensor';

import styles from './VideoHls.module.css';

class VideoHls extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialized: false,
      canplay: false
    };
    this.initVideo = this.initVideo.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.play = this.play.bind(this);
    this.stop = this.stop.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.getCurrentTime = this.getCurrentTime.bind(this);
    this.seekTo = this.seekTo.bind(this);
    this.pause = this.pause.bind(this);
    this.isPlaying = this.isPlaying.bind(this);
    this.videoRef = React.createRef();
  }

  onEnded(event) {
    const isEnded = event.currentTarget.currentTime === event.currentTarget.duration;
    if (isEnded && this.props.onEnded) {
      this.props.onEnded();
    }
  }

  onVisibilityChange(isVisible) {
    const { initialized } = this.state;
    const { playStopOnViewportChange } = this.props;
    if (isVisible && !initialized) {
      this.initVideo();
    }
    if (isVisible && initialized && playStopOnViewportChange) {
      this.play();
    }
    if (!isVisible && initialized && playStopOnViewportChange) {
      this.stop();
    }
  }

  getCurrentTime() {
    const video = this.videoRef.current;
    return video.currentTime;
  }

  play() {
    const video = this.videoRef.current;
    if (!this.isPlaying()) {
      video.play();
    }
  }

  pause() {
    const video = this.videoRef.current;
    if (this.isPlaying()) {
      video.pause();
    }
  }

  stop() {
    const video = this.videoRef.current;
    if (this.isPlaying()) {
      video.currentTime = 0;
      video.pause();
    }
  }

  isPlaying() {
    const video = this.videoRef.current;
    const isPlaying =
      video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
    return isPlaying;
  }

  initVideo() {
    const video = this.videoRef.current;
    const { autoPlay, src } = this.props;
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (autoPlay) {
          this.play();
        }
      });
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        this.setState({ canplay: true });
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.addEventListener('loadedmetadata', () => {
        if (autoPlay) {
          this.play();
        }
      });
      video.addEventListener('canplay', () => {
        this.setState({ canplay: true });
      });
    }
    this.setState({ initialized: true }, this.props.callback());
  }

  seekTo(currentTime) {
    const video = this.videoRef.current;
    video.currentTime = currentTime;
  }

  render() {
    const {
      handleEnded,
      autoPlay,
      poster,
      muted,
      controls,
      loop,
      playsinline,
      borderRadius,
      shadow,
      customPadding
    } = this.props;

    const { canplay } = this.state;
    return (
      <VisibilitySensor onChange={this.onVisibilityChange} scrollCheck partialVisibility>
        <div
          className={cn(
            { 'rounded-lg': borderRadius, 'shadow-2xl': shadow },
            'tw-video-wrapper h-full'
          )}
          style={{ paddingBottom: customPadding }}
        >
          <>
            {poster && (
              <img
                className={cn(
                  { [`${styles.hide}`]: canplay },
                  styles.image,
                  'w-full h-full absolute inset-0 z-10 object-cover transition-all duration-2000'
                )}
                src={poster}
                alt=""
              />
            )}
            <video
              autoPlay={autoPlay}
              muted={muted}
              loop={loop}
              controls={controls}
              ref={this.videoRef}
              onTimeUpdate={this.onEnded}
              onEnded={handleEnded}
              playsInline={playsinline}
              className="absolute object-cover w-full h-full t-0 l-0 r-0 b-0"
            />
          </>
        </div>
      </VisibilitySensor>
    );
  }
}

VideoHls.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string.isRequired,
  controls: PropTypes.bool,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  playStopOnViewportChange: PropTypes.bool,
  onEnded: PropTypes.func,
  handleEnded: PropTypes.func,
  playsinline: PropTypes.bool,
  callback: PropTypes.func,
  borderRadius: PropTypes.bool,
  shadow: PropTypes.bool,
  customPadding: PropTypes.string.isRequired
};

VideoHls.defaultProps = {
  controls: true,
  autoPlay: false,
  muted: false,
  loop: false,
  playStopOnViewportChange: false,
  onEnded: () => {},
  handleEnded: () => {},
  playsinline: false,
  callback: () => {},
  borderRadius: true,
  shadow: false
  // custonPadding: ''
};

export default VideoHls;
